import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  favoriteOrgsStorage,
  type FavoriteOrgId
} from '../utils/favoriteOrgsStorage'
import useUserInfo from './useUserInfo'

export interface UseFavoriteOrgsResult {
  favorites: Set<string>
  favoriteIds: FavoriteOrgId[]
  isFavorite: (orgId: FavoriteOrgId) => boolean
  toggleFavorite: (orgId: FavoriteOrgId) => void
  showOnly: boolean
  setShowOnly: (value: boolean) => void
}

/**
 * Hook for managing favorite organization IDs and the "favorites only"
 * filter state, persisted in localStorage keyed by the current user's
 * email (falls back to a global key when no email is available). IDs are
 * stored as strings internally so numeric and string IDs compare reliably.
 */
const useFavoriteOrgs = (): UseFavoriteOrgsResult => {
  const userInfo = useUserInfo()
  const email = userInfo?.email || null

  const [favoriteIds, setFavoriteIds] = useState<FavoriteOrgId[]>(() =>
    favoriteOrgsStorage.get(email)
  )
  const [showOnly, setShowOnlyState] = useState<boolean>(() =>
    favoriteOrgsStorage.getShowOnly(email)
  )

  // Re-sync from localStorage whenever the email changes. useUserInfo may
  // load asynchronously, so the initial value could be the global-key data;
  // once the real email resolves we must pick up that user's favorites.
  useEffect(() => {
    setFavoriteIds(favoriteOrgsStorage.get(email))
    setShowOnlyState(favoriteOrgsStorage.getShowOnly(email))
  }, [email])

  const favorites = useMemo(
    () => new Set(favoriteIds.map(String)),
    [favoriteIds]
  )

  const isFavorite = useCallback(
    (orgId: FavoriteOrgId) => favorites.has(String(orgId)),
    [favorites]
  )

  const toggleFavorite = useCallback(
    (orgId: FavoriteOrgId) => {
      const next = favoriteOrgsStorage.toggle(orgId, email)
      setFavoriteIds(next)
    },
    [email]
  )

  const setShowOnly = useCallback(
    (value: boolean) => {
      setShowOnlyState(prev => {
        if (prev === value) {
          return prev
        }
        favoriteOrgsStorage.setShowOnly(value, email)
        return value
      })
    },
    [email]
  )

  return {
    favorites,
    favoriteIds,
    isFavorite,
    toggleFavorite,
    showOnly,
    setShowOnly
  }
}

export default useFavoriteOrgs
